/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { Script } from 'gatsby';
import TrustArcCookieBanner from './src/components/TrustArcCookieBanner';
import RootStateProvider from './src/context/root-state.context';
import { StoreProvider } from './src/context/store-context';
import StoryblokProvider from './src/context/storyblok.context';
import TrustArcProvider from './src/context/trust-arc.context';
import { isInStoryblokEditor } from './src/utils/isInStoryblokEditor';
import ViewportProvider from './src/context/viewport.context';

// Only useful inside Storyblok's own visual-editor iframe — never needed for real traffic.
// isInStoryblokEditor() reads window.parent, which the server can't know at all (always false)
// and which the *editor iframe's* client would evaluate to true on its very first render,
// before hydration — branching on it directly in the render body caused a real mismatch for
// that one environment. Starting from `false` on both server and client, then correcting once
// mounted, avoids that; the effect fires practically instantly, so there's no meaningful delay
// for the one case (the editor) where this ever needs to become true.
function StoryblokBridgeScript() {
  const [inEditor, setInEditor] = useState(false);

  useEffect(() => {
    setInEditor(isInStoryblokEditor());
  }, []);

  if (!inEditor) {
    return null;
  }

  return (
    <Script
      id="storyblokBridge"
      type="text/javascript"
      referrerPolicy="strict-origin-when-cross-origin"
      src="//app.storyblok.com/f/storyblok-v2-latest.js"
    />
  );
}

const GTM_ID = 'GTM-WS56QD6';

// Hand-rolled equivalent of gatsby-plugin-google-tagmanager's injected snippet (removed from
// gatsby-config.js, see the comment there) — idle-deferred so it's off the critical path.
// Always loads (production only) — consent gating for the tags GTM fires happens inside the
// GTM container itself (Consent Mode / Additional Consent Checks), not in this app.
function GtmScript() {
  if (process.env.NODE_ENV !== 'production') {
    return null;
  }

  return (
    <Script
      id="gtm"
      strategy="idle"
      dangerouslySetInnerHTML={{
        __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${GTM_ID}');`,
      }}
    />
  );
}

// GTM now always loads (see GtmScript above) — consent gating for the tags it fires happens
// inside the GTM container itself. TrustArc is still loaded below for its own banner UI and to
// feed the Shopify and Loox consent decisions that remain gated client-side (see
// trustArcToShopifyConsent.js and LooxWidgetScript.tsx).
export const wrapPageElement = ({ element }) => {
  const hasEnabledTrustArc = !!process.env.GATSBY_TRUST_ARC_CM_ID;
  const trustArcUrl = `https://consent.trustarc.com/v2/notice/${process.env.GATSBY_TRUST_ARC_CM_ID}?c=teconsent`;

  return (
    <>
      <StoryblokProvider>
        <ViewportProvider>
          <RootStateProvider>{element}</RootStateProvider>
        </ViewportProvider>
      </StoryblokProvider>
      <StoryblokBridgeScript />
      {hasEnabledTrustArc && (
        <>
          <Script
            id="trustArc"
            type="text/javascript"
            referrerpolicy="strict-origin-when-cross-origin"
            src={trustArcUrl}
          />
          <TrustArcCookieBanner />
        </>
      )}
      <GtmScript />
    </>
  );
};

export const wrapRootElement = ({ element }) => (
  <TrustArcProvider>
    <StoreProvider>{element}</StoreProvider>
  </TrustArcProvider>
);
